import { isSamsungBrowser, isInMobile } from '@umu/umu-ua';
import { $document, $window, docElement } from './bom';

/**
 * setCss
 * via: zepto
 */
export interface ICSS {
    [prop: string]: any;
}

export const bodyEle = docElement;

export const cssNumber: { [prop: string]: number } = {
    'column-count': 1,
    columns: 1,
    'font-weight': 1,
    'line-height': 1,
    opacity: 1,
    'z-index': 1,
    zoom: 1
};

export function dasherize(str: string = '') {
    return str
        .replace(/::/g, '/')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
        .replace(/([a-z\d])([A-Z])/g, '$1_$2')
        .replace(/_/g, '-')
        .toLowerCase();
}

export function maybeAddPx(name: string, value: string | number): string | number {
    return typeof value === 'number' && !cssNumber[dasherize(name)] ? value + 'px' : value;
}

export function setCss(el: HTMLElement | null, property: ICSS): void {
    if (!el) {
        return;
    }

    let css = '';
    for (const key in property) {
        if (!property[key] && property[key] !== 0) {
            el.style.removeProperty(dasherize(key));
        } else {
            css += dasherize(key) + ':' + maybeAddPx(key, property[key]) + ';';
        }
    }

    el.style.cssText += ';' + css;
}

/**
 * classSet
 */
export interface IClassNameMap {
    [prop: string]: boolean;
}

export function classSet(...args: Array<IClassNameMap | string | undefined>): string {
    let names: string = '';
    const classNames = args[0];
    if (typeof classNames === 'object') {
        const nameMap: IClassNameMap = classNames as IClassNameMap;
        for (const name in nameMap) {
            if (nameMap.hasOwnProperty(name) && nameMap[name]) {
                names += name + ' ';
            }
        }
    } else {
        const nameList = args;
        for (let i = 0; i < nameList.length; i++) {
            if (nameList[i]) {
                names += nameList[i] + ' ';
            }
        }
    }

    return names.trim();
}

/**
 * 检测是否达到距离页面底部 offset 距离，主要用在滚动加载更多
 */
export function hitBottom(offset: number = 180, viewportEl?: Element, scrollerEl?: Element): boolean {
    let ret = false;

    let documentHeight = 0;
    if (viewportEl && scrollerEl) {
        if (viewportEl.scrollTop + viewportEl.clientHeight + offset > scrollerEl.clientHeight) {
            ret = true;
        }
    } else {
        documentHeight = $document.body.scrollHeight;
        const windowHeight = $window.innerHeight;
        const windowScrollTop =
            ($document.documentElement && $document.documentElement.scrollTop) || $document.body.scrollTop;

        if (windowHeight + windowScrollTop + offset >= documentHeight) {
            ret = true;
        }
    }
    return ret;
}

export function querySelector(selectors: string): Element | null {
    return $document.querySelector(selectors);
}

export const appendChild = (node: Element, selector: string) => {
    if (selector) {
        const parent = querySelector(selector);
        if (parent) {
            return parent.appendChild(node);
        }
        return null;
    } else {
        return $document.body.appendChild(node);
    }
};

// https://developers.google.com/web/updates/2016/06/passive-event-listeners?hl=zh-cn
export const isPassiveListenerSupported = () => {
    let supported = false;

    const testHandle = () => {
        // tslint:disable-next-line:no-console
        console.log('test handle');
    };

    try {
        const opts = Object.defineProperty({}, 'passive', {
            get() {
                supported = true;
            }
        });

        window.addEventListener('test', testHandle, opts);
        window.removeEventListener('test', testHandle, opts);
    } catch (e) {
        // tslint:disable-next-line:no-console
        console.log('dont Support passive');
    }

    return supported;
};

export const setTitle = (title: string) => {
    $document.title = title;
};

type TNode = Element | Node | null;
export const contains =
    // @ts-ignore
    $document.documentElement && $document.documentElement.contains
        ? (parent: TNode, node: TNode) => {
            return parent && node && parent !== node && parent.contains(node);
        }
        : (parent: TNode, node: TNode) => {
            if (parent && node) {
                while (node && node.parentNode) {
                    node = node.parentNode;
                    if (node === parent) {
                        return true;
                    }
                }
            }
            return false;
        };

/**
 * 查找滚动容器，通常情况是 window，但当浮层内局部滚动时为浮层内的滚动元素
 * via: https://stackoverflow.com/questions/35939886/find-first-scrollable-parent
 * @param node
 * @returns
 */
export function getScrollParent(node?: HTMLElement): HTMLElement | Window {
    if (!node) {
        return $window;
    }
    const isElement = node instanceof HTMLElement;
    const overflowY = isElement && window.getComputedStyle(node).overflowY;
    const isScrollable = overflowY !== 'visible' && overflowY !== 'hidden';

    if (isScrollable && node.scrollHeight >= node.clientHeight) {
        return node;
    }

    let ret = getScrollParent(node.parentNode as HTMLElement) || $window;
    if (ret === $document.body) {
        ret = $window;
    }
    return ret;
}

export function scrollTo(top: number, el?: HTMLElement, behavior?: ScrollBehavior) {
    const scrollEl = getScrollParent(el);
    try {
        scrollEl.scrollTo({
            top,
            behavior: behavior || 'auto'
        });
    } catch (e) {
        (scrollEl as HTMLElement).scrollTop = top;
    }
}

export function scrollToView(el?: HTMLElement, offset: number | string = 0) {
    if (!el) {
        return;
    }

    const rect = el.getBoundingClientRect();
    const parent = getScrollParent(el);
    let parentScrollTop = 0;
    if (parent !== $window) {
        parentScrollTop = (parent as HTMLElement).scrollTop;
    } else {
        parentScrollTop =
            ($document.documentElement && $document.documentElement.scrollTop) || $document.body.scrollTop;
    }
    const screenHeight = $window.innerHeight;

    // 百分比小数
    if (typeof offset === 'string') {
        if (offset.indexOf('%') === -1) {
            offset = 0;
        } else {
            offset = Number(offset.replace('%', '')) / 100;
        }
        offset = offset * screenHeight;
    }

    const top = rect.top + parentScrollTop;

    // 在视区内不滚动
    if (top >= parentScrollTop && top <= parentScrollTop + screenHeight) {
        return;
    }

    scrollTo(top + offset, el);
}

export function isElementInViewport1(target: HTMLElement, callback: () => void) {
    const options = {
        root: null,
        rootMargin: '0px',
        threshold: 1.0
    };
    if (!IntersectionObserver) {
        callback();
    }
    const observer = new IntersectionObserver(callback, options);
    observer.observe(target);
}

/**
 * 检查 element 是否在浏览器视窗内
 * @param el {HTMLElement} 目标元素
 * @param root {HTMLElement} 视窗或容器
 * @return boolean
 */
export function isElementInViewport(el: HTMLElement, root?: HTMLElement) {
    if (root) {
        if (el.offsetTop + el.offsetHeight >= root.offsetTop && root.offsetTop + root.offsetHeight >= root.offsetTop) {
            return true;
        }
        return false;
    }
    const rect = el.getBoundingClientRect();
    return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= ($window.innerHeight || $document.documentElement.clientHeight) &&
        rect.right <= ($window.innerWidth || $document.documentElement.clientWidth)
    );
}

/**
 * 检查 element 是否在浏览器视窗内或容器内（至少有一部分在内）
 * @param el {HTMLElement} 目标元素
 * @param root {HTMLElement} 视窗或容器
 * @return boolean
 */
export function isElementPartiallyInViewport(el: HTMLElement, root?: HTMLElement) {
    const viewportWidth = $window.innerWidth || $document.documentElement.clientWidth;
    const viewportHeight = $window.innerHeight || $document.documentElement.clientHeight;
    const rect = el.getBoundingClientRect();

    // 判断目标元素是否在容器元素内
    if (root) {
        const rootRect = root.getBoundingClientRect();
        return (
            rect.bottom > rootRect.top &&
            rect.right > rootRect.left &&
            rect.top < rootRect.bottom &&
            rect.left < rootRect.right
        );
    }

    // 判断目标元素是否在视口内
    return (
        rect.bottom > 0 &&
        rect.right > 0 &&
        rect.top < viewportHeight &&
        rect.left < viewportWidth
    );
}


/**
 * 是否浏览过，el距离底部为负值，表示已经看过了
 * @param el
 * @returns boolean
 */
export function isElementViewed(el: HTMLElement) {
    const rect = el.getBoundingClientRect();

    return (
        (rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= ($window.innerHeight || $document.documentElement.clientHeight)) ||
        rect.bottom <= 0 ||
        rect.right <= 0
    );
}

export const getDocScroller = () => {
    if ($document.scrollingElement) {
        return $document.scrollingElement;
    }
    if (isSamsungBrowser) {
        return $document.documentElement;
    }
    if (isInMobile()) {
        return $document.body;
    }
    return $document.documentElement;
};

// 目前是否有
export function isHasSelection() {
    if (!$window.getSelection) {
        return false;
    }
    const selection = $window.getSelection();
    if (selection && selection.toString().length > 0) {
        return true;
    }
    return false;
}

/**
 * 异步生成 script 并 promise 返回结果
 * @param paths string[]
 * @returns Promise
 */
export const loadScriptAsync = (paths: string[]) => {
    const container = document.body || document.head || document.documentElement;
    return Promise.all(
        paths.map(
            (path) =>
                new Promise((resolve, reject) => {
                    const script = document.createElement('script');
                    // script.async = true;
                    // script.defer = true;
                    script.type = 'text/javascript';
                    script.onload = () => resolve(path);
                    script.onerror = () => reject(new Error(path));
                    script.src = path;
                    container.appendChild(script);
                })
        )
    );
};

interface IPosition {
    top?: number;
    left?: number;
    bottom?: number;
}

/**
 * @desc 元素滚动到固定距离
 * @param position {left, top}
 * @param ele HTMLElement
 * @author Kody
 */
export const toScroll = (position: IPosition, ele?: HTMLElement, behavior: ScrollBehavior = 'smooth') => {
    ele = ele || docElement;
    const hasTop = position.hasOwnProperty('top');
    const hasLeft = position.hasOwnProperty('left');
    const hasBottom = position.hasOwnProperty('bottom');
    if (!hasTop && !hasLeft && !hasBottom) {
        return;
    }
    const nowTop = ele.scrollTop || 0;
    const top = hasBottom ? ele.scrollHeight - ele.offsetHeight - position.bottom! : hasTop ? position.top! : nowTop;
    const left = position.left || ele.scrollLeft || 0;
    const fnc: (x: number, y: number) => void = ele.scrollTo || ele.scroll;

    if (typeof fnc === 'function') {
        try {
            ele.scrollTo({
                top: top,
                left: left,
                behavior,
            });
        } catch (error) {
            fnc.call(ele, left, Number(top));
        }
    } else {
        if (hasTop || hasBottom) {
            ele.scrollTop = Number(top);
        }
        if (hasLeft) {
            ele.scrollLeft = left;
        }
    }
};


/**
 * 露出元素
 * @param {queryString | HTMLElement} target
 * @param {boolean | Object} alignToTop
 */
export const scrollIntoView = function (target: string | HTMLElement, alignToTop?: boolean) {
    if (!target) {
        return;
    }
    let element;
    if (typeof target === 'string') {
        element = document.querySelector(target);
    } else {
        element = target;
    }
    element && element.scrollIntoView && element.scrollIntoView(alignToTop === undefined ? true : alignToTop);
}
